/**
 * 采集批次列表管理 Hook
 * 用于获取和管理用户的素材包（采集批次）列表
 */

import { useState, useEffect, useCallback } from 'react'
import { listCrawlBatches } from '@/services/crawlBatchService'
import type {
  CrawlBatch,
  CrawlBatchPlatform,
} from '@/types/crawlBatch'

/**
 * useCrawlBatches Hook 的返回值类型
 */
export interface UseCrawlBatchesResult {
  /** 批次列表 */
  items: CrawlBatch[]
  /** 加载状态 */
  isLoading: boolean
  /** 错误信息 */
  error: string | null
  /** 总数 */
  total: number
  /** 当前页码 */
  page: number
  /** 每页数量 */
  pageSize: number
  /** 平台筛选 */
  platform: CrawlBatchPlatform | ''
  /** 备注搜索关键词 */
  remark: string
  /** 设置页码 */
  setPage: (page: number) => void
  /** 设置平台筛选 */
  setPlatform: (platform: CrawlBatchPlatform | '') => void
  /** 设置备注搜索 */
  setRemark: (remark: string) => void
  /** 刷新列表 */
  refresh: () => Promise<void>
}

/**
 * 采集批次列表管理 Hook
 * @param initialPageSize 初始每页数量，默认 20
 * @returns 批次列表状态和操作方法
 */
export function useCrawlBatches(initialPageSize = 20): UseCrawlBatchesResult {
  const [items, setItems] = useState<CrawlBatch[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const [pageSize] = useState<number>(initialPageSize)
  const [platform, setPlatform] = useState<CrawlBatchPlatform | ''>('')
  const [remark, setRemark] = useState<string>('')

  /**
   * 加载批次列表
   */
  const loadBatches = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setError(null)

    try {
      const params: {
        current: number
        pageSize: number
        platform?: CrawlBatchPlatform
        remark?: string
      } = {
        current: page,
        pageSize,
      }

      if (platform) {
        params.platform = platform
      }

      if (remark) {
        params.remark = remark
      }

      const response = await listCrawlBatches(params)

      setItems(response.list)
      setTotal(response.total)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '获取批次列表失败'
      setError(errorMessage)
      setItems([])
      setTotal(0)
    } finally {
      setIsLoading(false)
    }
  }, [page, pageSize, platform, remark])

  /**
   * 刷新列表
   */
  const refresh = useCallback(async (): Promise<void> => {
    await loadBatches()
  }, [loadBatches])

  /**
   * 监听依赖变化，自动加载数据
   */
  useEffect((): void => {
    void loadBatches()
  }, [loadBatches])

  return {
    items,
    isLoading,
    error,
    total,
    page,
    pageSize,
    platform,
    remark,
    setPage,
    setPlatform,
    setRemark,
    refresh,
  }
}
